import { Injectable, OnDestroy } from '@angular/core';
import { Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import { Store } from '@ngxs/store';
import { MapsHelperService } from '@map/services/maps-helper.service';
import { every } from 'lodash';
import { Event, NavigationEnd, Router } from '@angular/router';
import { FacilityCo2Type, FacilityH2Type } from '@enums';
import { BehaviorSubject } from 'rxjs';
import {
  LoadSelectedFacilityById,
  SetSelectedFacilityId,
} from '@appState/app.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from '@services';
import {
  RegisterCo2LocationComponent,
  RegisterH2LocationComponent,
} from '@facility';
import { TCO2FacilityExtendetDTO, TH2FacilityExtendetDTO, VOgeVnb } from '@api';
import { AppStateSelectors } from '@appState/app-state.selector';
import { AppViewEnum } from '../../../../shared/enums/app-view-enum';
import { filter } from 'rxjs/operators';
import { CustomAdvancedMarkerElement } from '../../../../shared/interfaces/custom-advanced-marker-element';

@UntilDestroy()
@Injectable()
export class MapsMarkerService implements OnDestroy {
  public readonly markers$ = new BehaviorSubject(null);
  private allMarkers: CustomAdvancedMarkerElement[] = [];
  private markerClusterer: MarkerClusterer;
  private isCo2View: boolean;
  private lastMarkerPosition = null;

  public constructor(
    private readonly store: Store,
    private readonly mapsHelperService: MapsHelperService,
    private readonly dialogService: DialogService,
    private readonly router: Router
  ) {
    this.handleListenerAfterPageChanged();
    this.store
      .select(AppStateSelectors.getAppView())
      .pipe(untilDestroyed(this))
      .subscribe(appView => {
        this.isCo2View = appView === AppViewEnum.CARBON_DIOXIDE;
      });
  }

  public ngOnDestroy(): void {
    this.markerClusterer?.clearMarkers();
    this.allMarkers = [];
    this.markers$.next([]);
    this.markers$.complete();
  }

  public setMarkersVisibility(
    activeDataType: number[],
    googlemap: google.maps.Map
  ): void {
    const appView = this.store.selectSnapshot(AppStateSelectors.getAppView());
    const isUserOge = this.store.selectSnapshot(AppStateSelectors.isUserOGE());
    if (this.allMarkers.length > 0 && this.markerClusterer) {
      this.markerClusterer.clearMarkers();

      this.allMarkers.map(m => {
        if (isUserOge && appView === AppViewEnum.HYDROGEN) {
          if (Object.hasOwn(m, 'vnb')) {
            m.map = googlemap;
          } else {
            // not VNB markers
            m.map = null;
          }
        } else {
          const typeId = Object.hasOwn(m, 'facility')
            ? m.facility.facility_type_id
            : null;
          if (!activeDataType?.includes(typeId)) {
            m.map = null;
          } else {
            m.map = googlemap;
          }
        }
        return m;
      });

      this.markerClusterer.addMarkers(
        this.allMarkers.filter(m => m.map !== null)
      );
      this.markerClusterer.render();

      // update markers
      this.markers$.next(this.allMarkers);
    }
  }

  public addFacilitiesMarker(
    googlemap: google.maps.Map,
    facilityList: TH2FacilityExtendetDTO[] | TCO2FacilityExtendetDTO[],
    activeDataType: number[]
  ): void {
    if (facilityList) {
      // sort list by facility_address
      const sortedList = [...facilityList].sort(
        (a, b) => b.facility_latitude - a.facility_latitude
      );
      sortedList.forEach(
        (facility: TH2FacilityExtendetDTO | TCO2FacilityExtendetDTO) => {
          this.removeOtherTypeOfMarker('vnb');
          this.removeDeletedFacilities();
          this.removeMarker(facility.facility_id);

          const marker: CustomAdvancedMarkerElement =
            new google.maps.marker.AdvancedMarkerElement({
              ...this.getMarkerOptionsByFacility(
                googlemap,
                facility,
                activeDataType
              ),
            });

          marker.oge_id = facility.facility_id;
          marker.facility = facility;
          this.addInfoWindowAndListenersToMarkerForMapPage(marker, 'facility');

          //if (!facility.facility_is_dangling) {
            this.allMarkers.push(marker);
          /*} else {
            this.removeMarker(facility.facility_id);
          }*/
        }
      );
      this.markers$.next(this.allMarkers);
    }
  }

  public addVNBMarker(googlemap: google.maps.Map, vnbs: VOgeVnb[]): void {
    if (vnbs) {
      vnbs.forEach(vnb => {
        this.removeOtherTypeOfMarker('facility');
        this.removeMarker(vnb.vnbId);

        const marker: CustomAdvancedMarkerElement =
          new google.maps.marker.AdvancedMarkerElement({
            ...this.getMarkerOptionsByVNB(googlemap, vnb),
          });

        marker.oge_id = vnb.vnbId;
        marker.vnb = vnb;
        this.addInfoWindowAndListenersToMarkerForMapPage(marker, 'vnb');

        this.allMarkers.push(marker);
      });
      this.markers$.next(this.allMarkers);
    }
  }

  public addMarkerCluster(
    googlemap: google.maps.Map,
    activeDataType: number[],
    withFitBounds?: boolean
  ): void {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    if (this.allMarkers.length > 0) {
      this.markerClusterer = new MarkerClusterer({
        map: googlemap,
        markers: this.allMarkers,
        renderer: {
          render({ count, position, markers }): Marker {
            // create custom pin element
            const img = document.createElement('img');
            const clusterElement = document.createElement('div');
            clusterElement.classList.add('pin-element');
            const countElement = document.createElement('span');
            countElement.innerText = count.toString();
            // add elements
            clusterElement.appendChild(img);
            clusterElement.appendChild(countElement);

            // use the correct img for the types
            img.src = '/assets/cluster_mixed.svg';
            if (every(markers, 'vnb')) {
              img.src = '/assets/cluster_vnb.svg';
            } else {
              const isH2Facility =
                activeDataType.includes(FacilityH2Type.Consumer) ||
                activeDataType.includes(FacilityH2Type.Producer);
              const markersAreConsumers = every(markers, [
                'facility.facility_type_id',
                isH2Facility
                  ? FacilityH2Type.Consumer
                  : FacilityCo2Type.Ausspeisung,
              ]);
              const markersAreProducers = every(markers, [
                'facility.facility_type_id',
                isH2Facility
                  ? FacilityH2Type.Producer
                  : FacilityCo2Type.Einspeisung,
              ]);
              if (markersAreConsumers) {
                img.src = '/assets/cluster_consumer.svg';
              } else if (markersAreProducers) {
                img.src = '/assets/cluster_producer.svg';
              }
              // else is mixed
            }

            const marker: CustomAdvancedMarkerElement =
              new google.maps.marker.AdvancedMarkerElement({
                position,
                zIndex: 999,
                map: count > 1 ? googlemap : null,
                content: clusterElement,
                gmpClickable: true,
              });
            /* when marker clickable (map page) show cluster infos */
            if (
              (markers[0] as google.maps.marker.AdvancedMarkerElement).element
                .style.pointerEvents === 'auto'
            ) {
              const content = markers
                .map(m => {
                  if (Object.hasOwn(m, 'facility')) {
                    return (m as CustomAdvancedMarkerElement).facility
                      .facility_name;
                  }
                  if (Object.hasOwn(m, 'vnb')) {
                    // for oge
                    return (m as CustomAdvancedMarkerElement).vnb.vnbName;
                  }
                  return 'test';
                })
                .join(',</br>');
              const infoWindow = new google.maps.InfoWindow({
                ariaLabel: 'Cluster Informationen',
                minWidth: 200,
                disableAutoPan: true,
                content: `<p>${content}</p>`,
              });

              marker.addEventListener('mouseover', () => {
                infoWindow.open(
                  {
                    map: googlemap,
                    shouldFocus: false,
                  },
                  marker
                );
              });

              marker.addEventListener('mouseout', () => {
                infoWindow.close();
              });
            }
            return marker;
          },
        },
      });
      this.markers$.next(this.allMarkers);
    }

    if (withFitBounds) {
      this.fitBoundsForMarker(googlemap);
    }
  }

  private getMarkerOptionsByFacility(
    googlemap: google.maps.Map,
    facility: TH2FacilityExtendetDTO,
    activeDataType: number[]
  ): google.maps.marker.AdvancedMarkerElementOptions {
    let offset = 0;

    if (this.lastMarkerPosition) {
      if (
        this.lastMarkerPosition.lat === facility.facility_latitude &&
        this.lastMarkerPosition.lng === facility.facility_longitude
      ) {
        offset = Math.random() / 3000;
      } else {
        offset = 0;
      }
    }
    // always set last marker position
    this.lastMarkerPosition = {
      lat: facility.facility_latitude,
      lng: facility.facility_longitude,
    };

    return {
      map: activeDataType.includes(facility.facility_type_id)
        ? googlemap
        : null,
      content: this.createIconElement(facility),
      gmpClickable: this.router.url.includes('/karte'),
      position: {
        lat: facility.facility_latitude + offset,
        lng: facility.facility_longitude + offset,
      },
    };
  }

  private createIconElement(facility: TH2FacilityExtendetDTO) {
    const img = document.createElement('img');
    let url = '';
    if (
      facility.facility_type_id === FacilityH2Type.Consumer ||
      facility.facility_type_id === FacilityCo2Type.Ausspeisung
    ) {
      url = 'assets/place_consumer.svg';
    } else if (facility.facility_type_id === FacilityCo2Type.Hybrid) {
      url = 'assets/place_hybrid.svg';
    } else {
      url = 'assets/place_producer.svg';
    }
    img.src = url;
    return img;
  }

  private getMarkerOptionsByVNB(
    googlemap: google.maps.Map,
    vnb: VOgeVnb
  ): google.maps.marker.AdvancedMarkerElementOptions {
    const img = document.createElement('img');
    img.src = 'assets/place_vnb.svg';
    return {
      map: googlemap,
      gmpClickable: this.router.url.includes('/karte'),
      content: img,
      position: {
        lat: vnb.vnbLocationLongitude,
        lng: vnb.vnbLocationLatitude,
      },
    };
  }

  private removeOtherTypeOfMarker(entityString: string): void {
    this.allMarkers.forEach((m, index) => {
      if (Object.hasOwn(m, entityString)) {
        this.allMarkers.splice(index, 1);
      }
    });
  }

  private removeMarker(searchId: number) {
    if (this.allMarkers.length > 0) {
      // remove marker when exists... so the marker is up-to-date (after edit)
      const markerExistsIndex = this.allMarkers.findIndex(
        m => m.oge_id === searchId
      );

      if (markerExistsIndex != -1) {
        this.allMarkers.splice(markerExistsIndex, 1);
      }
    }
  }

  private addInfoWindowAndListenersToMarkerForMapPage(
    marker: CustomAdvancedMarkerElement,
    type: string
  ): void {
    if (this.router.url.includes('/karte')) {
      const data = marker[type];
      const infoWindow = new google.maps.InfoWindow(
        type == 'facility'
          ? this.getFacilityMarkerContent(data)
          : this.getVNBMarkerContent(data)
      );

      marker.addEventListener('mouseover', () => {
        if (!marker.infowWindowIsOpen) {
          if (marker.map) {
            infoWindow.open(
              {
                map: marker.map,
                shouldFocus: false,
              },
              marker as google.maps.marker.AdvancedMarkerElement
            );
            marker.infowWindowIsOpen = true;
          }
        }
      });

      marker.addEventListener('mouseout', () => {
        infoWindow.close();
        marker.infowWindowIsOpen = false;
      });
      if (type === 'facility') {
        marker.gmpClickable = this.router.url.includes('/karte');
        marker.addEventListener('gmp-click', () => {
          this.store.dispatch(new SetSelectedFacilityId(data.facility_id));
          this.store.dispatch(new LoadSelectedFacilityById());
          if (this.isCo2View) {
            this.dialogService.openRegisterLocation(
              RegisterCo2LocationComponent
            );
          } else {
            this.dialogService.openRegisterLocation(
              RegisterH2LocationComponent
            );
          }
        });
      }
    }
  }

  private getFacilityMarkerContent(
    facility: TH2FacilityExtendetDTO | TCO2FacilityExtendetDTO
  ) {
    let companyName = `</br>${
      (facility as TH2FacilityExtendetDTO)?.facility_vnb_company_name === undefined
        ? (facility as TCO2FacilityExtendetDTO)?.facility_company_name
        : (facility as TH2FacilityExtendetDTO)?.facility_vnb_company_name
    }`;
    const userCompany = this.store.selectSnapshot(
      AppStateSelectors.getUsersCompany()
    );
    if (Object.values(FacilityCo2Type).includes(facility?.facility_type_id)) {
      // co2
      if (
        userCompany?.company_name ===
        (facility as TCO2FacilityExtendetDTO).facility_company_name
      ) {
        companyName = '';
      }
    } else {
      // should h2 and vnb?
      if (
        userCompany?.company_name !==
        (facility as TH2FacilityExtendetDTO).facility_owner_company_name
      ) {
        companyName = `</br>${(facility as TH2FacilityExtendetDTO).facility_owner_company_name}`;
      }
    }
    return {
      ariaLabel: 'Standort Infomationen',
      minWidth: 200,
      maxWidth: 300,
      disableAutoPan: true,
      content: `
            <p><strong>${facility.facility_name}</strong>
            </br>${facility.facility_street} ${facility.facility_street_number}
            </br>${facility.facility_zip_code} ${facility.facility_city}
            ${companyName}</p>
            `,
    };
  }

  private getVNBMarkerContent(vnb: VOgeVnb) {
    return {
      ariaLabel: 'VNB Infomationen',
      minWidth: 200,
      maxWidth: 300,
      disableAutoPan: true,
      content: `
            <p><strong>${vnb.vnbName}</strong>
            </br>${vnb.vnbLocationStreet} ${vnb.vnbLocationStreetNumber}
            </br>${vnb.vnbLocationZipCode} ${vnb.vnbLocationCity}
            `,
    };
  }

  private fitBoundsForMarker(googlemap: google.maps.Map): void {
    const latLngArray: google.maps.LatLngLiteral[] = [];
    this.allMarkers.forEach(m => {
      latLngArray.push({
        lat: m.position.lat as number,
        lng: m.position.lng as number,
      });
    });
    this.mapsHelperService.setFocus(googlemap, null, latLngArray);
  }

  private handleListenerAfterPageChanged(): void {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(
          (routerEvent: Event): routerEvent is NavigationEnd =>
            routerEvent instanceof NavigationEnd
        )
      )
      .subscribe(result => {
        this.allMarkers.forEach(m => {
          if (result.url.includes('/karte')) {
            this.addInfoWindowAndListenersToMarkerForMapPage(m, 'facility');
          } else {
            this.resetMarkerListeners(m);
          }
        });
        this.markers$.next(this.allMarkers);
      });
  }

  private resetMarkerListeners(marker: CustomAdvancedMarkerElement) {
    marker.gmpClickable = false;
    marker.addEventListener('mouseover', () => {});
    marker.addEventListener('mouseout', () => {});
    marker.addEventListener('gmp-click', () => {});
  }

  private removeDeletedFacilities(): void {
    const allFacilities: TCO2FacilityExtendetDTO[] | TH2FacilityExtendetDTO[] =
      this.store.selectSnapshot(AppStateSelectors.getFacilityList());

    this.allMarkers.forEach((m, index) => {
      const foundedFacility = allFacilities.find(
        faci => faci.facility_id === m.oge_id
      );
      if (foundedFacility === undefined) {
        this.allMarkers.splice(index, 1);
      }
    });
  }
}
